import styled from "@emotion/styled"
import {
	PButton,
	PButtonPure,
	PIcon,
	PInlineNotification,
	PLinkPure,
	PText,
} from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorContrastLower,
	radiusXl,
	shadowSm,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
	spacingStaticXl,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import { defineMessages, FormattedMessage } from "react-intl"

const messages = defineMessages({
	countSelected: {
		defaultMessage: "{value, plural, one {# vehicle selected} other {# vehicles selected}}",
		id: "inventorylist.vehicleSharingBar.countSelected",
	},
	openVehiclesButton: {
		defaultMessage: "{value, plural, one {Open vehicle} other {Open vehicles}}",
		id: "inventorylist.vehicleSharingBar.openVehiclesButton",
	},
	shareVehiclesLink: {
		defaultMessage: "{value, plural, one {Share vehicle} other {Share vehicles}}",
		id: "inventorylist.vehicleSharingBar.shareVehiclesLink",
	},
	tooManyVehiclesHint: {
		defaultMessage:
			"The link can contain up to {max} vehicles. Deselect {count, plural, one {# vehicle} other {# vehicles}} to share.",
		id: "inventorylist.vehicleSharingBar.tooManyVehiclesHint",
	},
})

interface VehicleSharingBarProps {
	className?: string
	count: number
	customerFinderUrl?: string
	maxCount?: number
	onCloseClick: () => void
	onOpenVehiclesClick: () => void
	onShareVehiclesClick: () => void
}

export const VehicleSharingBar = ({
	className,
	count,
	customerFinderUrl,
	maxCount,
	onCloseClick,
	onOpenVehiclesClick,
	onShareVehiclesClick,
}: VehicleSharingBarProps) => {
	const exceedingCount = maxCount === undefined ? 0 : Math.max(count - maxCount, 0)
	const isLimitExceeded = exceedingCount > 0

	return (
		<Container className={className}>
			<Row>
				<CountWrapper>
					<PIcon name="success-filled" />
					<PText weight="semibold">
						<FormattedMessage {...messages.countSelected} values={{ value: count }} />
					</PText>
				</CountWrapper>

				<PButtonPure icon="upload" alignLabel="start" disabled={isLimitExceeded} onClick={onShareVehiclesClick}>
					<FormattedMessage {...messages.shareVehiclesLink} values={{ value: count }} />
				</PButtonPure>
				{isLimitExceeded ? (
					<PButtonPure icon="external" alignLabel="start" disabled>
						<FormattedMessage {...messages.openVehiclesButton} values={{ value: count }} />
					</PButtonPure>
				) : (
					<PLinkPure
						href={customerFinderUrl}
						target="_blank"
						icon="external"
						alignLabel="start"
						onClick={onOpenVehiclesClick}
					>
						<FormattedMessage {...messages.openVehiclesButton} values={{ value: count }} />
					</PLinkPure>
				)}

				<CloseButton variant="secondary" compact icon="close" hideLabel onClick={onCloseClick} />
			</Row>

			{isLimitExceeded && (
				<PInlineNotification state="warning" dismissButton={false}>
					<FormattedMessage
						{...messages.tooManyVehiclesHint}
						values={{ count: exceedingCount, max: maxCount }}
					/>
				</PInlineNotification>
			)}
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	justify-self: center;
	width: auto;
	gap: ${spacingStaticMd};
	padding: ${spacingStaticMd} ${spacingStaticLg};
	background: ${colorContrastLower};
	backdrop-filter: blur(32px);
	border-radius: ${radiusXl};
	box-shadow: ${shadowSm};

	@media (max-width: ${breakpoint.s}px) {
		position: relative;
		padding: ${spacingStaticMd};
	}
`

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
	gap: ${spacingStaticXl};

	@media (max-width: ${breakpoint.s}px) {
		display: grid;
		grid-template-columns: auto auto;
		justify-items: center;
		gap: ${spacingStaticSm} ${spacingStaticMd};
	}
`

const CountWrapper = styled.div`
	display: flex;
	gap: ${spacingStaticXs};
	
	@media (max-width: ${breakpoint.s}px) {
		grid-column: 1 / -1;
	}
`

const CloseButton = styled(PButton)`
	@media (max-width: ${breakpoint.s}px) {
		position: absolute;
		top: ${spacingStaticSm};
		inset-inline-end: ${spacingStaticSm};
	}
`
